import { AppAPIName } from 'src/constants';
import { API } from 'src/utils/AmplifyApiUtils';

import { ApiTags, appAPI } from '.';

// Mirrors backend/lib/resources/studio_credit/web_api.go — the Studio pricing
// catalog rendered for this portal. Prices/lookup keys come from the code
// catalog; credits reflect the portal's pinned grant ladder.
export interface StudioPlanTier {
  tier: number;
  credits: number;
  monthlyCents: number;
  /** Absent for tiers not sold annually (Freemium). */
  annualCents?: number;
  monthlyLookupKey: string;
  annualLookupKey?: string;
}

export type StudioPlanId = 'freemium' | 'starter' | 'professional' | 'advanced';

export interface StudioPlan {
  plan: StudioPlanId;
  tiers: StudioPlanTier[];
}

export interface StudioPlansResponse {
  catalogVersion: string;
  plans: StudioPlan[];
}

// Mirrors backend/lib/resources/studio_credit/web_api.go CreditBalanceResponse —
// the display subset of the workspace's balance row. `creditBalance` and
// `monthlyCreditGrant` are millicredits (1000 = 1 credit); divide before
// showing them. `periodEnd` is the "credits reset on {date}" the Plans page
// renders. The same shape arrives over websocket (entity STUDIO_CREDIT_BALANCE)
// to keep the balance real time — see websocket/creditBalanceHandler.ts.
export interface CreditBalanceResponse {
  creditBalance: number;
  monthlyCreditGrant: number;
  periodStart: string;
  periodEnd: string;
}

// Mirrors backend/lib/resources/studio_credit/web_api.go CurrentPlanResponse —
// the portal's current plan resolved from its live Stripe subscription, for
// all plan families. Studio subscriptions carry the parsed selection (plan,
// interval, credits = included + extras); Classic ones carry the accurate
// display name (fixes the empty-name heuristics for `_ent` customs and
// anonymous `price_1…` prices). `family: 'none'` means no billable
// subscription resolved (trial, cancelled, or no Stripe customer yet).
export type CurrentPlanFamily = 'studio' | 'classic' | 'none';

export interface CurrentPlanResponse {
  family: CurrentPlanFamily;
  plan?: StudioPlanId;
  /** 'monthly' | 'annual' for Studio; the Stripe interval for Classic. */
  interval?: string;
  credits?: number;
  includedCredits?: number;
  extraCredits?: number;
  planDisplayName?: string;
  renewalDate?: string;
  cancelAtPeriodEnd: boolean;
}

export const studioCreditApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getStudioPlans: build.query<StudioPlansResponse, void>({
      queryFn: async () => {
        try {
          const data = await API.get(AppAPIName, '/studio-plans', {});
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.studioPlans],
    }),
    getCurrentPlan: build.query<CurrentPlanResponse, void>({
      queryFn: async () => {
        try {
          const data = await API.get(AppAPIName, '/current-plan', {});
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.currentPlan],
    }),
    getCreditBalance: build.query<CreditBalanceResponse, void>({
      queryFn: async () => {
        try {
          const data = await API.get(AppAPIName, '/credit-balance', {});
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.creditBalance],
    }),
  }),
});

export const {
  useGetStudioPlansQuery,
  useGetCreditBalanceQuery,
  useGetCurrentPlanQuery,
} = studioCreditApi;
